import { useState, useCallback } from 'react';
import * as faceapi from 'face-api.js';

let modelsLoaded = false;
let modelsLoading: Promise<void> | null = null;

const MODEL_URL = '/models';

async function loadModelsOnce(): Promise<void> {
	if (modelsLoaded) return;
	if (modelsLoading) return modelsLoading;

	modelsLoading = (async () => {
		try {
			await Promise.all([
				faceapi.nets.tinyFaceDetector.loadFromUri(MODEL_URL),
				faceapi.nets.faceLandmark68Net.loadFromUri(MODEL_URL),
				faceapi.nets.faceRecognitionNet.loadFromUri(MODEL_URL),
			]);
			modelsLoaded = true;
		} catch (err) {
			modelsLoading = null; // allow retry on next call
			throw err;
		}
	})();

	return modelsLoading;
}

export function useFaceDetection() {
	const [isModelLoaded, setIsModelLoaded] = useState(modelsLoaded);
	const [isDetecting, setIsDetecting] = useState(false);
	const [error, setError] = useState<string | null>(null);

	const loadModels = useCallback(async () => {
		try {
			setError(null);
			await loadModelsOnce();
			setIsModelLoaded(true);
		} catch {
			setError('Failed to load face detection models');
		}
	}, []);

	const detectFace = useCallback(
		async (video: HTMLVideoElement): Promise<number[] | null> => {
			if (!modelsLoaded) {
				setError('Models not loaded');
				return null;
			}

			setIsDetecting(true);
			setError(null);

			try {
				const detection = await faceapi
					.detectSingleFace(video, new faceapi.TinyFaceDetectorOptions())
					.withFaceLandmarks()
					.withFaceDescriptor();

				if (!detection) {
					setError('No face detected');
					return null;
				}

				return Array.from(detection.descriptor);
			} catch {
				setError('Face detection failed');
				return null;
			} finally {
				setIsDetecting(false);
			}
		},
		[],
	);

	return { isModelLoaded, isDetecting, error, loadModels, detectFace };
}
